import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Zap, Sparkles, ChevronDown, ChevronUp, Copy } from "lucide-react";

/** Per-release promo-link editor. The stored URLs auto-inject into every
 *  caption generated by the weekly batch and the 7-day Release Day Blast. */
export default function ReleasePromoEditor({ release, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    hypeddit_url: release.hypeddit_url || "",
    spotify_presave_url: release.spotify_presave_url || "",
    smart_link_url: release.smart_link_url || "",
    promo_notes: release.promo_notes || "",
  });

  const hasPromo =
    release.hypeddit_url || release.spotify_presave_url || release.smart_link_url;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/releases/${release.release_id}/promo`, form);
      toast.success("Promo links saved — they'll auto-inject into every new caption.");
      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Save failed";
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard?.writeText(url).then(
      () => toast("Copied"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="mt-3 pt-4 border-t border-white/[0.06]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="label-mono w-full flex items-center justify-between text-white/55 hover:text-white transition-colors"
        data-testid={`promo-toggle-${release.release_id}`}
      >
        <span className="flex items-center gap-2">
          <Zap size={11} className={hasPromo ? "text-[#FF3B00]" : ""} />
          Promo links {hasPromo && <span className="text-[#FF3B00]">· active</span>}
        </span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>

      {!open && hasPromo && (
        <div className="mt-2 space-y-1">
          {release.hypeddit_url && (
            <div className="flex items-center gap-2 text-xs text-white/60 font-mono truncate">
              <span className="text-[#FF3B00]">Hypeddit</span>
              <span className="truncate">{release.hypeddit_url}</span>
              <button className="opacity-60 hover:opacity-100 shrink-0"
                      onClick={() => copyLink(release.hypeddit_url)}>
                <Copy size={10} />
              </button>
            </div>
          )}
          {release.spotify_presave_url && (
            <div className="flex items-center gap-2 text-xs text-white/60 font-mono truncate">
              <span className="text-[#1DB954]">Spotify</span>
              <span className="truncate">{release.spotify_presave_url}</span>
              <button className="opacity-60 hover:opacity-100 shrink-0"
                      onClick={() => copyLink(release.spotify_presave_url)}>
                <Copy size={10} />
              </button>
            </div>
          )}
          {release.smart_link_url && (
            <div className="flex items-center gap-2 text-xs text-white/60 font-mono truncate">
              <span className="text-white/70">Smart link</span>
              <span className="truncate">{release.smart_link_url}</span>
              <button className="opacity-60 hover:opacity-100 shrink-0"
                      onClick={() => copyLink(release.smart_link_url)}>
                <Copy size={10} />
              </button>
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="label-mono text-white/45 flex items-center gap-2 mb-1.5">
              <Link2 size={10} /> Hypeddit gate (primary CTA)
            </label>
            <input
              className="mp-input"
              type="url"
              placeholder="https://hypeddit.com/alchemistca/fadinghorizon"
              value={form.hypeddit_url}
              onChange={(e) => setForm({ ...form, hypeddit_url: e.target.value })}
              data-testid={`promo-hypeddit-${release.release_id}`}
            />
          </div>
          <div>
            <label className="label-mono text-white/45 flex items-center gap-2 mb-1.5">
              <span className="w-2 h-2 rounded-full bg-[#1DB954]" /> Spotify pre-save URL
            </label>
            <input
              className="mp-input"
              type="url"
              placeholder="https://open.spotify.com/album/... or https://presave.link/..."
              value={form.spotify_presave_url}
              onChange={(e) => setForm({ ...form, spotify_presave_url: e.target.value })}
              data-testid={`promo-presave-${release.release_id}`}
            />
          </div>
          <div>
            <label className="label-mono text-white/45 flex items-center gap-2 mb-1.5">
              <Sparkles size={10} /> Smart link (linkfire / toneden / show.co)
            </label>
            <input
              className="mp-input"
              type="url"
              placeholder="https://lnk.to/fadinghorizon"
              value={form.smart_link_url}
              onChange={(e) => setForm({ ...form, smart_link_url: e.target.value })}
              data-testid={`promo-smartlink-${release.release_id}`}
            />
          </div>
          <div>
            <label className="label-mono text-white/45 mb-1.5 block">
              Context for the AI (optional)
            </label>
            <textarea
              className="mp-textarea"
              rows={2}
              maxLength={400}
              placeholder="e.g. Afterlife-inspired melodic techno, collab with producer X, follow-up to 'Fading Horizon'"
              value={form.promo_notes}
              onChange={(e) => setForm({ ...form, promo_notes: e.target.value })}
              data-testid={`promo-notes-${release.release_id}`}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="label-mono text-white/35 leading-relaxed">
              Priority: Hypeddit → Smart link → Spotify. The first match is auto-appended as "🎧 Pre-save: …" before release day, or "🎧 Stream: …" after.
            </div>
            <button
              className="btn-amber-small shrink-0"
              onClick={save}
              disabled={saving}
              data-testid={`promo-save-${release.release_id}`}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
